import { useField } from "formik";
import type { ComponentProps, ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface FormFieldProps extends Omit<ComponentProps<typeof Input>, "name"> {
  name: string;
  label: string;
  /** Leading icon rendered inside the input, e.g. `<FiMail className="size-4" />`. */
  icon?: ReactNode;
  /** Trailing element inside the input, e.g. a password-visibility toggle button. */
  endAdornment?: ReactNode;
  /** Extra element next to the label, e.g. a "Forgot password?" link. */
  labelAdornment?: ReactNode;
  /** Override the label's classes, e.g. the uppercase micro-label used on data-entry grids. */
  labelClassName?: string;
}

/**
 * Formik-bound input built on shadcn's Input/Label. Wire every form field
 * through this (or an equivalent) so validation errors render consistently
 * instead of being handled ad hoc per form.
 */
export function FormField({
  name,
  label,
  className,
  icon,
  endAdornment,
  labelAdornment,
  labelClassName,
  ...props
}: FormFieldProps) {
  const [field, meta] = useField(name);
  const hasError = meta.touched && !!meta.error;

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={name} className={labelClassName}>
          {label}
        </Label>
        {labelAdornment}
      </div>
      <div className="relative">
        {icon && (
          <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
            {icon}
          </span>
        )}
        <Input
          id={name}
          aria-invalid={hasError}
          className={cn(icon && "pl-10", endAdornment && "pr-10", className)}
          {...field}
          {...props}
        />
        {endAdornment && (
          <span className="absolute top-1/2 right-3 -translate-y-1/2">{endAdornment}</span>
        )}
      </div>
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}
